import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Stack,
  alpha,
  Chip,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Payments as PaymentsIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LinkIcon from '@mui/icons-material/Link';
import RepeatIcon from '@mui/icons-material/Repeat';
import { useTenantConfig } from '../hooks/useTenant';

const FinansMuhasebePage = () => {
  const navigate = useNavigate();
  const { tenantName } = useTenantConfig();

  const mainFeatures = [
    {
      image: '/assets/icon/budget.webp',
      title: 'Gelir & Gider Yönetimi',
      desc: 'Bağış, aidat ve diğer gelirler ile tüm giderleri tek finansal yapı içinde yönetin.',
      color: '#f97316'
    },
    {
      image: '/assets/icon/cash.webp',
      title: 'Kasa ve Banka Takibi',
      desc: 'Kasa ve banka hesap bakiyelerini, para hareketleriyle birlikte anlık izleyin.',
      color: '#0891b2'
    },
    {
      image: '/assets/icon/money.webp',
      title: 'Bağış Gelir Kalemleri',
      desc: 'Bağışları proje, faaliyet veya genel gelir olarak ayırarak şeffaf biçimde takip edin.',
      color: '#f43f5e'
    },
    {
      image: '/assets/icon/budget2.webp',
      title: 'Bütçe Planlama',
      desc: 'Planlanan ve gerçekleşen gelir–giderleri karşılaştırarak bütçenizi kontrol edin.',
      color: '#8b5cf6'
    },
    {
      image: '/assets/icon/card-machine.webp',
      title: 'POS & Online Tahsilat',
      desc: 'Sanal POS ve banka entegrasyonlarıyla bağış ve ödemeleri otomatik tahsil edin.',
      color: '#10b981'
    },
    {
      image: '/assets/icon/expense.webp',
      title: 'Masraf & Avans Takibi',
      desc: 'Masraf, avans ve harcamaları belgeleyerek düzenli ve denetlenebilir şekilde yönetin.',
      color: '#eab308'
    },
    {
      image: '/assets/icon/asset.webp',
      title: 'Demirbaş Yönetimi',
      desc: 'Dernek envanterindeki demirbaşları tek ekrandan yönetin ve takip edin.',
      color: '#6366f1'
    },
    {
      image: '/assets/icon/analysis.webp',
      title: 'Detaylı Raporlama',
      desc: 'Tüm finansal verileri tarih, kategori ve işlem türüne göre raporlayın.',
      color: '#ec4899'
    }
  ];

  // Süreç adımları
  const processSteps = [
    { step: '1', text: 'Bağış alındı → gelir kalemine kaydedildi', color: '#f43f5e' },
    { step: '2', text: 'Aidat tahsil edildi → gelir olarak işlendi', color: '#f97316' },
    { step: '3', text: 'Kasa/Banka hareketi otomatik eşlendi', color: '#eab308' },
    { step: '4', text: 'Masraf girildi → gider kaydedildi', color: '#10b981' },
    { step: '5', text: 'Bütçe planına yansıdı', color: '#0891b2' },
    { step: '6', text: 'Rapor üretildi', color: '#8b5cf6' }
  ];

  // Güven özellikleri
  const trustFeatures = [
    { icon: <VerifiedIcon />, text: 'Gelir-gider takibi kayıt altındadır' },
    { icon: <SecurityIcon />, text: 'Banka & kasa kayıtları loglanır' },
    { icon: <AssessmentIcon />, text: 'Denetime hazır raporlar' },
    { icon: <CheckCircleIcon />, text: 'KVKK uyumlu veri saklama' }
  ];

  return (
    <Box>
      {/* 1️⃣ HERO BÖLÜMÜ */}
      <Box
        sx={{
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
          background: `linear-gradient(135deg, ${alpha('#f97316', 0.06)} 0%, #ffffff 100%)`,
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        {/* Background Decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha('#f97316', 0.12)} 0%, transparent 70%)`,
            filter: 'blur(80px)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box maxWidth="720px">
            {/* Category Chip */}
            <Chip
              label="Finans & Muhasebe Modülü"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: '0.8rem',
                background: alpha('#f97316', 0.12),
                color: '#ea580c',
                border: `1px solid ${alpha('#f97316', 0.2)}`,
              }}
            />

            {/* Title */}
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 3,
                lineHeight: 1.1,
                color: '#0f172a',
                letterSpacing: '-0.02em',
              }}
            >
              Finansal İşlemleri Sadece Kaydetmeyin,{' '}
              <Box component="span" sx={{
                background: `linear-gradient(135deg, #f97316 0%, #fb923c 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Sistemin Merkezine Alın
              </Box>
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                color: '#64748b',
                lineHeight: 1.7,
                mb: 5,
              }}
            >
              Aidat, bağış, gider ve nakit akışını tek sistem üzerinden yönetin.
              Excel tablolarına, parçalı kayıtlara ve manuel kontrollere son verin.
            </Typography>

            {/* CTA Buttons */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button
                variant="contained"
                size="large"
                 //onClick={() => navigate('/basvuru')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  bgcolor: '#f97316',
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(249, 115, 22, 0.35)',
                  '&:hover': {
                    bgcolor: '#ea580c',
                    boxShadow: '0 6px 20px rgba(249, 115, 22, 0.45)',
                  },
                }}
              >
                Hemen Başlayın
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* 3️⃣ ÇÖZÜM YAKLAŞIMI */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">

            {/* Sol: Metin ve Özellikler */}
            <Grid item xs={12} md={6}>
              <Typography variant="h3" fontWeight={700} mb={2} color="#0f172a">
                Gelir–Giderinizi <br />
                Tek Sistemde Yönetin
              </Typography>

              <Typography
                sx={{
                  fontSize: '1.05rem',
                  color: '#475569',
                  lineHeight: 1.8,
                  mb: 4,
                  maxWidth: 520,
                }}
              >
                Bağışlar, aidatlar, giderler, kasa ve banka hesapları tek merkezde toplanır.
                Derneğinizin tüm mali yapısını anlık izleyin, kontrol edin ve raporlayın.
              </Typography>

              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ color: '#f97316', display: 'flex' }}>
                    <PaymentsIcon />
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: '#334155' }}>
                    Bağış ve aidat gelirleri
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ color: '#f97316', display: 'flex' }}>
                    <ReceiptIcon />
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: '#334155' }}>
                    Gider ve masraf takibi
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ color: '#f97316', display: 'flex' }}>
                    <AccountBalanceIcon />
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: '#334155' }}>
                    Kasa & banka bakiyeleri
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ color: '#f97316', display: 'flex' }}>
                    <TrendingUpIcon />
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: '#334155' }}>
                    Bütçe planlama ve raporlar
                  </Typography>
                </Stack>
              </Stack>
            </Grid>

            {/* Sağ: Sistem Akış Paneli */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 700,
                  height: { xs: 300, md: 470 },
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  border: '1px solid #e5e7eb',
                  background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                  mx: 'auto',
                }}
              >
                <Box
                  component={'img'}
                  src='/assets/page_images/gelir_gider.webp'
                  alt="Finansal Akış Şeması"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box
        sx={{
          py: { xs: 8, md: 8 },
          backgroundColor: '#f5f6f8',
        }}
      >
        <Container maxWidth="lg" mb={4}>
          {/* BAŞLIK */}
          <Box textAlign="center" mb={8}>
            <Typography variant="h3" fontWeight={700} mb={2} color="#0f172a">
              Finansal Süreç Nasıl İşler?
            </Typography>

            <Typography variant="body1" color="text.secondary">
              {tenantName}’te gelir, gider, aidat ve muhasebe kayıtları
              tek bir finansal akış içinde birbirine bağlanır.
            </Typography>
          </Box>

          <Grid container spacing={6} alignItems="center">
            {/* SOL: ŞEMA / GÖRSEL ALANI */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 360,
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #eef2ff, #f8fafc)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                <Box
                  component={'img'}
                  src='/assets/page_images/sema.webp'
                  alt="Finansal Akış Şeması"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Box>
            </Grid>

            {/* SAĞ: ADIM ADIM ANLATIM */}
            <Grid item xs={12} md={6}>
              <Stack spacing={4}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TrendingUpIcon sx={{ color: '#ef4444', mt: '4px' }} />
                  <Box>
                    <Typography fontWeight={600}>
                      Gelir ve Gider Kalemlerini Oluşturun
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Gelir ve giderlerinizi gruplar ve kategoriler altında tanımlayarak
                      finansal yapınızı netleştirin.
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <LinkIcon sx={{ color: '#ef4444', mt: '4px' }} />
                  <Box>
                    <Typography fontWeight={600}>
                      Gelir ve Giderleri Birbirine Bağlayın
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Bağışları, masrafları ve diğer işlemleri ilgili kalemlerle eşleştirerek
                      kayıtlarınızı hızlıca oluşturun.
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <RepeatIcon sx={{ color: '#ef4444', mt: '4px' }} />
                  <Box>
                    <Typography fontWeight={600}>
                      Aidatları Düzenli Gelir Olarak Tanımlayın
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tekrarlayan aidat ve ödemeleri tanımlayarak düzenli gelir–gider
                      takibini otomatik hale getirin.
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
      {/* 4️⃣ ANA ÖZELLİKLER */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" fontWeight={700} mb={2} color="#0f172a">
              Kapsamlı Finans Yönetimi Özellikleri
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ color: 'text.secondary', fontSize: '1.1rem' }}
            >
              Her özellik, dernek ihtiyaçlarına özel tasarlanmıştır
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {mainFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                      borderColor: feature.color
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Box
                      sx={{
                        padding: 1.5,
                        width: 75,
                        height: 75,
                        borderRadius: '12px',
                        bgcolor: alpha(feature.color, 0.1),
                        color: feature.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      <img src={feature.image} alt={feature.title} style={{ width: '100%', height: '100%' }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mb: 1.5, fontSize: '1rem' }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6, fontSize: '0.9rem' }}
                    >
                      {feature.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      {/* 5️⃣ SÜREÇ SENARYOSU */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f5f6f8' }}>
        <Container maxWidth="lg" mb={4}>
          <Box textAlign="center" mb={8}>
            <Typography variant="h3" fontWeight={700} mb={2} color="#0f172a">
              Bir Finansal İşlem {tenantName}’te Nasıl İlerler?
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ color: 'text.secondary', fontSize: '1.1rem' }}
            >
              Otomatik akış ve entegrasyon sayesinde tüm süreç birbirine bağlıdır
            </Typography>
          </Box>

          <Grid container spacing={3} width={'100%'} mx="auto">
            {processSteps.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'scale(1.02)',
                      bgcolor: 'white'
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: 20,
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: '#f97316',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '1.1rem'
                    }}
                  >
                    {step.step}
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ mt: 2, fontWeight: 500, lineHeight: 1.6 }}
                  >
                    {step.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box textAlign="center" mt={5}>
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.85rem',
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}
            >
              Her adım otomatik kayıt altına alınır ve raporlanabilir
            </Typography>
          </Box>
        </Container>
      </Box>





      {/* 6️⃣ GÜVEN & DENETİM */}
      <Box sx={{ py: { xs: 10, md: 14 }, background: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Chip
              label="GÜVEN & GÜVENLİK"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: '0.8rem',
                background: alpha('#64748b', 0.12),
                color: '#475569',
                border: 'none'
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                fontWeight: 800,
                mb: 2,
                color: '#0f172a'
              }}
            >
              Kontrol Sizde, Kayıtlar Sisteminizde
            </Typography>
            <Typography
              sx={{
                color: '#64748b',
                fontSize: '1.05rem',
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              Güvenli, şeffaf ve denetlenebilir finansal yönetim
            </Typography>
          </Box>


          {/* Trust Features */}
          <Grid container spacing={3} maxWidth="800px" width={'100%'} mx="auto">
            {trustFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderRadius: 3,
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: alpha('#64748b', 0.3),
                      transform: 'translateX(4px)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: alpha('#64748b', 0.1),
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      '& svg': { fontSize: 22 }
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#334155' }}>
                    {feature.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* KVKK Badge */}
          <Box textAlign="center" mt={6}>
            <Paper
              elevation={0}
              sx={{
                display: 'inline-block',
                px: 5,
                py: 3,
                background: 'white',
                border: '2px solid #e2e8f0',
                borderRadius: 3,
                maxWidth: '600px'
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  color: '#64748b'
                }}
              >
                Tüm finansal verileriniz güvenli altyapıda, şifreli ve yedekli olarak saklanır
              </Typography>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* 7️⃣ KAPANIŞ CTA */}
      <Box
        sx={{
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              background: `linear-gradient(135deg, #f97316 0%, #ea580c 50%, #dc2626 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 4,
              py: { xs: 6, md: 8 },
              px: { xs: 3, md: 6 },
            }}
          >
            {/* Arka plan deseni */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.15,
                backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                backgroundSize: '30px 30px'
              }}
            />

            {/* Decorative circles */}
            <Box
              sx={{
                position: 'absolute',
                top: '-10%',
                right: '-5%',
                width: 400,
                height: 400,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                filter: 'blur(60px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '-10%',
                left: '-5%',
                width: 300,
                height: 300,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                filter: 'blur(50px)',
              }}
            />

            <Box sx={{ position: 'relative', textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  fontWeight: 900,
                  mb: 3,
                  textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
              >
                Finansal Kontrolü Elinize Alın
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.2rem' },
                  mb: 5,
                  opacity: 0.95,
                  maxWidth: '580px',
                  mx: 'auto',
                  lineHeight: 1.7,
                  color: 'rgba(255,255,255,0.9)'
                }}
              >
                Bağış, aidat ve giderleri tek sistemde takip edin. Muhasebe aracı değil — dernek finans sisteminiz.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                 //onClick={() => navigate('/basvuru')}
                  sx={{
                    bgcolor: 'white',
                    color: '#f97316',
                    px: 5,
                    py: 2,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: '#fafafa',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  Hemen Başvurun
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/iletisim')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 5,
                    py: 2,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    borderWidth: 2,
                    backdropFilter: 'blur(10px)',
                    background: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      borderColor: 'white',
                      borderWidth: 2,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  İletişime Geçin
                </Button>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FinansMuhasebePage;

